import React from "react";
import { useSharedValue } from "../components/Constants/constants";
import { headerStyle, sectionStyle } from "./formConstants";
import { FormCheckBox, NormalFormDate, FormInput, FormTitle } from "./common";
import "./style.css";
import { useSelfCertIndividualForm } from "../Hooks/useSelfCertIndividualForm";
import { SelfCertIndividualService } from "../services/SelfCertIndividualService";

interface SelfCertFormProps {
  selectedRowData?: any;
  row?: any;
  selectedIndex?: number;
  onSaveSuccess?: (row: any) => void;
}

const SelfCertificationForm = ({
  selectedRowData,
  row,
  selectedIndex = 0,
  onSaveSuccess,
}: SelfCertFormProps) => {
  const { isMenuOpen } = useSharedValue();

  // Use the hook for form management
  const { model, isSaving, updateField, updateMultipleFields, validate } =
    useSelfCertIndividualForm(selectedRowData);

  // Helper function to get field values from model
  const getFieldValue = (fieldName: string): any => {
    const value = model[fieldName] || "";
    // console.log(`🔍 getFieldValue("${fieldName}") = "${value}"`);
    return value;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    // Update the model using the hook
    updateField(name, fieldValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("🚨 SelfCertIndividual handleSubmit called");

    try {
      // Use the model as the source of truth
      const currentFormValues = { ...model };
      currentFormValues.isMapDone = true;

      console.log("🚨 SelfCertIndividual currentFormValues:", currentFormValues);

      // Create save model with all form data
      const saveModel = new (model.constructor as any)(currentFormValues);

      console.log("🚨 SelfCertIndividual saveModel:", saveModel);

      // Save using the service
      const result = await SelfCertIndividualService.saveToApi(saveModel, row, selectedIndex);

      console.log("🚨 SelfCertIndividual save result:", result);

      if (result.success) {
        console.log("✅ SelfCertIndividual Form saved successfully");
        if (onSaveSuccess) {
          onSaveSuccess(result.updatedRow);
        }
        alert("Form saved successfully");
      } else {
        console.error("❌ SelfCertIndividual Failed to save form:", result.error);
        alert("Failed to save form: " + result.error);
      }
    } catch (error) {
      console.error("❌ SelfCertIndividual Error saving form:", error);
      alert("Error saving form: " + error);
    }
  };

  return (
    <div className="scroll-container" style={headerStyle(isMenuOpen)}>
      <div
        style={{ width: "100%", display: "flex", flexDirection: "column", padding: 0, margin: 0 }}
      >
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100% !important",
            minWidth: "100%",
            maxWidth: "100%",
            flex: "1 1 100%",
            display: "flex !important",
            flexDirection: "column",
            margin: "0 !important",
            padding: "0 !important",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", width: "100%", flexDirection: "column" }}>
            {/* Section I: Identification of Individual */}
            <section style={sectionStyle}>
              <FormTitle value={"Section I – Identification of Individual"} />

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Title"}
                    id={"SC_Individual_Title"}
                    name={"SC_Individual_Title"}
                    value={getFieldValue("SC_Individual_Title")}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: "3" }}>
                  <FormInput
                    label={"Last Name*"}
                    id={"SC_Individual_Last_name"}
                    name={"SC_Individual_Last_name"}
                    value={getFieldValue("SC_Individual_Last_name")}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"First Name*"}
                    id={"SC_Individual_First_name"}
                    name={"SC_Individual_First_name"}
                    value={getFieldValue("SC_Individual_First_name")}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Middle Name"}
                    id={"SC_Individual_Middle_name"}
                    name={"SC_Individual_Middle_name"}
                    value={getFieldValue("SC_Individual_Middle_name")}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Town/City of Birth*"}
                    id={"SC_Individual_City_of_birth"}
                    name={"SC_Individual_City_of_birth"}
                    value={getFieldValue("SC_Individual_City_of_birth")}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Country of Birth*"}
                    id={"SC_Individual_Country_of_birth"}
                    name={"SC_Individual_Country_of_birth"}
                    value={getFieldValue("SC_Individual_Country_of_birth")}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <NormalFormDate
                    label={"Date of Birth (DD/MM/YYYY)*"}
                    name={"SC_Individual_Date_of_birth"}
                    value={getFieldValue("SC_Individual_Date_of_birth")}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <FormTitle value={"8. Current Residence Address"} />

              <FormInput
                label={"House/Apt/Suite Name/Number/Street"}
                id={"SC_Individual_Current_residence_address_street"}
                name={"SC_Individual_Current_residence_address_street"}
                value={getFieldValue("SC_Individual_Current_residence_address_street")}
                onChange={handleChange}
              />

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Town/City*"}
                    id={"SC_Individual_Current_residence_address_city"}
                    name={"SC_Individual_Current_residence_address_city"}
                    value={getFieldValue("SC_Individual_Current_residence_address_city")}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Province/State*"}
                    id={"SC_Individual_Current_residence_address_state"}
                    name={"SC_Individual_Current_residence_address_state"}
                    value={getFieldValue("SC_Individual_Current_residence_address_state")}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Country*"}
                    id={"SC_Individual_Current_residence_address_country"}
                    name={"SC_Individual_Current_residence_address_country"}
                    value={getFieldValue("SC_Individual_Current_residence_address_country")}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Postal Code"}
                    id={"SC_Individual_Current_residence_address_postal"}
                    name={"SC_Individual_Current_residence_address_postal"}
                    value={getFieldValue("SC_Individual_Current_residence_address_postal")}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <FormTitle value={"9. Mailing Address (If different)"} />

              <FormInput
                label={"House/Apt/Suite Name/Number/Street"}
                id={"SC_Individual_Mailing_address_street"}
                name={"SC_Individual_Mailing_address_street"}
                value={getFieldValue("SC_Individual_Mailing_address_street")}
                onChange={handleChange}
              />

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Town/City"}
                    id={"SC_Individual_Mailing_address_city"}
                    name={"SC_Individual_Mailing_address_city"}
                    value={getFieldValue("SC_Individual_Mailing_address_city")}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Province/State"}
                    id={"SC_Individual_Mailing_address_state"}
                    name={"SC_Individual_Mailing_address_state"}
                    value={getFieldValue("SC_Individual_Mailing_address_state")}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Country"}
                    id={"SC_Individual_Mailing_address_country"}
                    name={"SC_Individual_Mailing_address_country"}
                    value={getFieldValue("SC_Individual_Mailing_address_country")}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Postal Code"}
                    id={"SC_Individual_Mailing_address_postal"}
                    name={"SC_Individual_Mailing_address_postal"}
                    value={getFieldValue("SC_Individual_Mailing_address_postal")}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </section>

            {/* Section II: Tax Residency */}
            <section style={sectionStyle}>
              <FormTitle value={"Section II – Tax Residency"} />

              <p style={{ marginBottom: 15 }}>
                I hereby certify that the Individual identified above is resident for tax purposes
                in the following jurisdiction(s) and the individual's Taxpayer Identification Number
                (TIN) or functional equivalent in its jurisdiction of residence for tax purposes is:
              </p>

              <FormInput
                label={"Jurisdiction 1*"}
                id={"sc_cp Jurisdiction 1"}
                name={"sc_cp Jurisdiction 1"}
                value={getFieldValue("sc_cp Jurisdiction 1")}
                onChange={handleChange}
                required
              />

              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Tax reference number type"}
                    id={"sc_cp Tax reference number type"}
                    name={"sc_cp Tax reference number type"}
                    value={getFieldValue("sc_cp Tax reference number type")}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <FormInput
                    label={"Tax Identification Number*"}
                    id={"sc_cp Tax Identification Number"}
                    name={"sc_cp Tax Identification Number"}
                    value={getFieldValue("sc_cp Tax Identification Number")}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", marginBottom: 15 }}>
                <FormCheckBox
                  label={
                    "Check this box if this jurisdiction does not issue or you are unable to procure a TIN or functional equivalent"
                  }
                  name={"sc_cp Unable to procure TIN"}
                  checked={getFieldValue("sc_cp Unable to procure TIN")}
                  onChange={handleChange}
                />
              </div>

              <FormInput
                label={"Specify the reason for non-availability of TIN"}
                id={"sc_cp Reason for non-availability of TIN"}
                name={"sc_cp Reason for non-availability of TIN"}
                value={getFieldValue("sc_cp Reason for non-availability of TIN")}
                onChange={handleChange}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 20,
                  marginTop: 10,
                  flexDirection: "row",
                }}
              >
                <p>
                  I certify that for the purposes of taxation, the Account Holder is not a tax
                  resident in any other country than Jurisdiction 1*
                </p>
                <div style={{ width: "6px" }} />
                <FormCheckBox
                  label={"Yes"}
                  name={"sc_cp Sole Residency Yes"}
                  checked={getFieldValue("sc_cp Sole Residency Yes")}
                  onChange={handleChange}
                />
                <div style={{ width: "6px" }} />
                <FormCheckBox
                  label={"No"}
                  name={"sc_cp Sole Residency No"}
                  checked={getFieldValue("sc_cp Sole Residency No")}
                  onChange={handleChange}
                />
              </div>

              <FormTitle
                value={"12. Additional Jurisdictions of Residency for Tax Purposes (if applicable)"}
              />

              {/* Table for additional jurisdictions */}
              <div style={{ overflowX: "auto", marginBottom: "20px" }}>
                <table
                  style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ddd" }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Jurisdiction
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Tax Identification Number
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Tax Reference Number Type
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                        Reason for non-availability of TIN (if applicable)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Row 1 - Jurisdiction 2 */}
                    <tr>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Jurisdiction 2"
                          name="sc_cp Jurisdiction 2"
                          value={getFieldValue("sc_cp Jurisdiction 2")}
                          onChange={handleChange}
                          placeholder="Select jurisdiction"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Tax Identification Number 2"
                          name="sc_cp Tax Identification Number 2"
                          value={getFieldValue("sc_cp Tax Identification Number 2")}
                          onChange={handleChange}
                          placeholder="Enter tax ID"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Tax Reference Number Type 2"
                          name="sc_cp Tax Reference Number Type 2"
                          value={getFieldValue("sc_cp Tax Reference Number Type 2")}
                          onChange={handleChange}
                          placeholder="Enter reference type"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Reason for non-availability of TIN 2"
                          name="sc_cp Reason for non-availability of TIN 2"
                          value={getFieldValue("sc_cp Reason for non-availability of TIN 2")}
                          onChange={handleChange}
                          placeholder="Enter reason if applicable"
                        />
                      </td>
                    </tr>
                    {/* Row 2 - Jurisdiction 3 */}
                    <tr>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Jurisdiction 3"
                          name="sc_cp Jurisdiction 3"
                          value={getFieldValue("sc_cp Jurisdiction 3")}
                          onChange={handleChange}
                          placeholder="Select jurisdiction"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Tax Identification Number 3"
                          name="sc_cp Tax Identification Number 3"
                          value={getFieldValue("sc_cp Tax Identification Number 3")}
                          onChange={handleChange}
                          placeholder="Enter tax ID"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Tax Reference Number Type 3"
                          name="sc_cp Tax Reference Number Type 3"
                          value={getFieldValue("sc_cp Tax Reference Number Type 3")}
                          onChange={handleChange}
                          placeholder="Enter reference type"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Reason for non-availability of TIN 3"
                          name="sc_cp Reason for non-availability of TIN 3"
                          value={getFieldValue("sc_cp Reason for non-availability of TIN 3")}
                          onChange={handleChange}
                          placeholder="Enter reason if applicable"
                        />
                      </td>
                    </tr>
                    {/* Row 3 - Jurisdiction 4 */}
                    <tr>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Jurisdiction 4"
                          name="sc_cp Jurisdiction 4"
                          value={getFieldValue("sc_cp Jurisdiction 4")}
                          onChange={handleChange}
                          placeholder="Select jurisdiction"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Tax Identification Number 4"
                          name="sc_cp Tax Identification Number 4"
                          value={getFieldValue("sc_cp Tax Identification Number 4")}
                          onChange={handleChange}
                          placeholder="Enter tax ID"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Tax Reference Number Type 4"
                          name="sc_cp Tax Reference Number Type 4"
                          value={getFieldValue("sc_cp Tax Reference Number Type 4")}
                          onChange={handleChange}
                          placeholder="Enter reference type"
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <FormInput
                          label=""
                          id="sc_cp Reason for non-availability of TIN 4"
                          name="sc_cp Reason for non-availability of TIN 4"
                          value={getFieldValue("sc_cp Reason for non-availability of TIN 4")}
                          onChange={handleChange}
                          placeholder="Enter reason if applicable"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section III: Declaration and Signature */}
            <section style={sectionStyle}>
              <FormTitle value={"Section III – Declaration and Signature"} />

              <FormInput
                label={"Signed Name"}
                id={"SC_Individual_Signer_printed_name"}
                name={"SC_Individual_Signer_printed_name"}
                value={getFieldValue("SC_Individual_Print_name")}
                onChange={handleChange}
                required
              />

              <NormalFormDate
                label={"Date (DD/MM/YYYY)*"}
                name={"SC_Individual_Date"}
                value={getFieldValue("SC_Individual_Date")}
                onChange={handleChange}
              />

              <FormInput
                label={
                  "If you have signed this on behalf of the Controlling Person please indicate the capacity in which you have acted here*"
                }
                id={"SC_Individual_Capacity"}
                name={"SC_Individual_Capacity"}
                value={getFieldValue("SC_Individual_Capacity")}
                onChange={handleChange}
              />

              <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                <input
                  type="checkbox"
                  id="SC_Individual_Signature_unreadable"
                  checked={!!getFieldValue("SC_Individual_Signature_unreadable")}
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "SC_Individual_Signature_unreadable",
                        type: "checkbox",
                        checked: e.target.checked,
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{ marginRight: 8 }}
                />
                <label htmlFor="SC_Individual_Signature_unreadable" style={{ fontWeight: 500 }}>
                  Check if signature is unreadable
                </label>
              </div>
            </section>

            <div
              style={{
                display: "flex",
                marginTop: 20,
                marginBottom: 40,
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                maxWidth: "100%",
                alignItems: "center",
                boxSizing: "border-box",
              }}
            >
              <button
                type="submit"
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#3B82F6",
                  color: "white",
                  fontWeight: 500,
                  marginLeft: "auto",
                  marginRight: "auto",
                  borderRadius: "0.375rem",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  cursor: "pointer",
                  border: "none",
                }}
              >
                Save Form
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SelfCertificationForm;
